import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { whatsappLink } from '@/mocks/gymInfo';

const features = [
  'home.about.feature1',
  'home.about.feature2',
  'home.about.feature3',
  'home.about.feature4',
];

export default function AboutSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 80}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="reveal-item reveal font-body text-sm text-foreground-500 uppercase tracking-widest">
            {t('home.about.label')}
          </span>
          <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 mt-3 leading-tight">
            {t('home.about.title')}
          </h2>
          <p className="reveal-item reveal font-body text-base md:text-lg text-foreground-700 mt-6 max-w-2xl mx-auto leading-relaxed">
            {t('home.about.text')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 max-w-2xl mx-auto">
            {features.map((key, idx) => (
              <div key={key} className={`reveal-item reveal flex items-center justify-center gap-3`} style={{ transitionDelay: `${(idx + 4) * 80}ms` }}>
                <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                  <i className="ri-check-line text-base"></i>
                </div>
                <span className="font-body text-sm text-foreground-700">
                  {t(key)}
                </span>
              </div>
            ))}
          </div>

          <div className="reveal-item reveal flex flex-col sm:flex-row justify-center gap-3 mt-8">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hover inline-flex items-center justify-center gap-2 bg-foreground-950 hover:bg-foreground-900 text-background-50 font-body text-sm font-semibold px-6 py-3 rounded-full whitespace-nowrap"
            >
              {t('home.about.cta')}
              <i className="ri-arrow-right-line text-base btn-icon-move"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}